import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../utils";

import {
	MultipleSelectInput,
	MultipleSelectInputProps,
} from "../../../../../molecules/SelectInputs/MultipleSelectInput";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from MultipleSelectInput that aren't in BaseFormFieldProps
type UniqueInputProps<T> = Omit<
	MultipleSelectInputProps<T>,
	keyof BaseFormFieldProps<T[]> | "value" | "onChange" | "onBlur"
>;

export type FormMultipleSelectProps<T> = Omit<BaseFormFieldProps<T[]>, "input"> & UniqueInputProps<T>;

/**
 * This is a form component that is used to create a multiple select dropdown.
 * It is a wrapper around the MultipleSelectInput component.
 * It is used to create a multiple select dropdown that is connected to the PyForm context.
 * Its props cross-cut with the MultipleSelectInput component and the FormField component.
 *
 * MultipleSelectInput is a component for multiple selection inputs using Radix UI
 * Supports both 'select' (non-searchable) and 'combobox' (searchable) variants
 * Uses chips to display selected items
 */
export const FormMultipleSelect = <T,>(props: FormMultipleSelectProps<T>) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	if (!form) {
		throw new Error("FormMultipleSelect must be used within a Form component");
	}

	return (
		<FormField<T[]>
			{...inputWrapperProps}
			input={(field) => {
				return (
					<MultipleSelectInput
						{...inputProps}
						value={field.value || []}
						onChange={field.onChange}
						id={inputWrapperProps.id}
						data-testid={`${inputWrapperProps["data-testid"]}-multiple-select`}
						disabled={inputWrapperProps.disabled}
					/>
				);
			}}
		/>
	);
};

export default FormMultipleSelect;
